import { useAuth } from '@clerk/clerk-react';
import { CalendarClock, Download } from 'lucide-react';
import { useMemo, useState } from 'react';
import { API_BASE_URL } from '../api/client.js';
import { useMe } from '../api/useMe.js';
import { useOffShiftVisitsReport } from '../api/useReports.js';
import { EmptyState } from '../components/EmptyState.js';
import { cn } from '../lib/cn.js';

const DAY_MS = 24 * 60 * 60_000;

function toDateInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function dateToWindowIso(dateStr: string, endOfDay: boolean): string {
  const d = new Date(dateStr);
  if (endOfDay) d.setHours(23, 59, 59, 999);
  return d.toISOString();
}
function fmt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function OffShiftVisitsReport() {
  const me = useMe();
  const today = new Date();
  const twoWeeksAgo = new Date(today.getTime() - 14 * DAY_MS);
  const [fromDate, setFromDate] = useState(toDateInputValue(twoWeeksAgo));
  const [toDate, setToDate] = useState(toDateInputValue(today));
  const fromIso = useMemo(() => dateToWindowIso(fromDate, false), [fromDate]);
  const toIso = useMemo(() => dateToWindowIso(toDate, true), [toDate]);
  const report = useOffShiftVisitsReport({ from: fromIso, to: toIso }, Boolean(me.data));
  const rows = report.data?.rows ?? [];

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-5 flex items-end justify-between">
        <div>
          <span className="eyebrow">Reports</span>
          <h1 className="mt-1 font-heading text-[22px] font-bold tracking-tightest text-ink">
            Off-shift visits
          </h1>
          <p className="text-sm text-ink-faint">
            Visits a worker attended while not on an Empower shift. No welfare check-in is opened in
            that case, so each is flagged here for review.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <DateField label="From" value={fromDate} onChange={setFromDate} />
          <DateField label="To" value={toDate} onChange={setToDate} />
          <CsvDownloadButton from={fromIso} to={toIso} />
        </div>
      </div>

      {report.isError && (
        <Banner kind="error">Failed to load report: {(report.error as Error).message}</Banner>
      )}
      {report.isLoading && (
        <div className="panel p-8 text-center text-sm text-ink-faint">Loading…</div>
      )}
      {!report.isLoading && rows.length === 0 && !report.isError && (
        <EmptyState
          icon={CalendarClock}
          title="No off-shift visits in this window"
          description="No worker attended a visit off-shift for these dates. Try a wider window."
        />
      )}

      {rows.length > 0 && (
        <div className="panel">
          <table className="min-w-full divide-y divide-line">
            <thead className="bg-surface-3">
              <tr>
                <Th>Worker</Th>
                <Th>Client</Th>
                <Th>Scheduled start</Th>
                <Th>Arrived</Th>
                <Th>Logged</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line bg-white">
              {rows.map((r) => (
                <tr
                  key={`${r.visit_id ?? 'novisit'}-${r.occurred_at}`}
                  className="hover:bg-surface-3"
                >
                  <Td>
                    <div className="text-sm font-medium text-ink">
                      {r.display_name ?? 'Unknown'}
                    </div>
                    {r.email && <div className="text-xs text-ink-faint">{r.email}</div>}
                  </Td>
                  <Td className="text-ink-dim">{r.client_display_name ?? '—'}</Td>
                  <Td className="tabular-nums text-ink-dim">{fmt(r.scheduled_start)}</Td>
                  <Td className="tabular-nums text-ink-dim">{fmt(r.actual_arrival)}</Td>
                  <Td className="tabular-nums text-ink-faint">{fmt(r.occurred_at)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="mb-1 block text-[11px] font-medium text-ink-faint">{label}</label>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-line-strong bg-white px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
      />
    </div>
  );
}

function Banner({ kind, children }: { kind: 'error'; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'mb-4 rounded-md border px-4 py-3 text-sm',
        kind === 'error' && 'border-red-200 bg-red-50 text-red-800',
      )}
    >
      {children}
    </div>
  );
}

function CsvDownloadButton({ from, to }: { from: string; to: string }) {
  const { getToken } = useAuth();
  const [busy, setBusy] = useState(false);
  async function onClick() {
    setBusy(true);
    try {
      const token = await getToken();
      const sp = new URLSearchParams({ from, to });
      const res = await fetch(`${API_BASE_URL}/reports/off-shift-visits.csv?${sp.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `off-shift-visits-${from.slice(0, 10)}-to-${to.slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setBusy(false);
    }
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="inline-flex h-9 items-center gap-1.5 rounded-md border border-line-strong bg-white px-3 text-sm font-medium text-ink-dim transition hover:bg-surface-3 disabled:opacity-50"
      title="Download as CSV"
    >
      <Download className="h-3.5 w-3.5" />
      {busy ? 'Preparing…' : 'CSV'}
    </button>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn(
        'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-ink-faint',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-4 py-3 text-sm', className)}>{children}</td>;
}
